import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Card,
  useTheme,
  useMediaQuery,

} from "@mui/material";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Breadcrumbs from "examples/Breadcrumbs";

import bgBanner from "assets/images/bg-presentation.jpg";
import bgFilosofia from "assets/images/banner-parceiros.png";
import NossaHistoria from "./Historia";
import NossaFilosofia from "./NossaFilosofia";
import WhatsAppFloat from "components/WhatsAppFloat";

/* ----------------- Paleta Ramlis ----------------- */
const BRAND = {
  main: "#036D3D", // verde principal
  dark: "#004C2A", // verde escuro (hover/sombras)
  glass: "rgba(1,43,24,.58)", // fundo translúcido para glass
  line: "rgba(3,109,61,.30)", // linhas/trilhos
  ring: "rgba(3,109,61,.55)", // bordas/indicadores
};

/* ----------------- Helpers ----------------- */
function SectionContainer({ children, sx }) {
  return (
    <Container sx={{ maxWidth: 1100, width: "100%", mx: "auto", ...sx }}>
      {children}
    </Container>
  );
}
SectionContainer.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

const FadeIn = ({ children, show, timeout = 400, sx }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [show]);

  return (
    <MKBox
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${timeout}ms ease, transform ${timeout}ms ease`,
        ...sx,
      }}
    >
      {children}
    </MKBox>
  );
};
FadeIn.propTypes = {
  children: PropTypes.node,
  show: PropTypes.bool,
  timeout: PropTypes.number,
  sx: PropTypes.object,
};

/* ----------------- Página ----------------- */
export default function Institucional() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loaded, setLoaded] = useState(false);
  const NAVBAR_PT = { xs: 18, md: 25 }; // respiro para navbar fixa

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const parallax = isMobile ? "scroll" : "fixed";

  return (
    <>
      <MKBox
        role="img"
        aria-label="Ramlis Machine - Usinagem de precisão"
        sx={{
          position: "relative",
          pt: NAVBAR_PT,
          minHeight: { xs: 410, md: 400 },
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          color: "#fff",
          backgroundImage: `url(${bgBanner})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center 35%" },
          backgroundAttachment: { xs: "scroll", md: parallax },
          "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            // scrim no verde da marca para contraste
            background:
              "linear-gradient(180deg, rgba(0, 14, 8, 0.75) 0%, rgba(0,76,42,.55) 45%, rgba(0,76,42,.75) 100%)",
          },
        }}
      >
        <FadeIn show={loaded} timeout={600} sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          <SectionContainer>
            {/* Plaquinha glass atrás do texto */}
            <MKBox
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                px: { xs: 1.5, md: 3 },
                py: { xs: 1, md: 1.25 },
                mx: "auto",
                borderRadius: 2,
                backgroundColor: BRAND.glass,
                backdropFilter: "saturate(1.1) blur(3px)",
                boxShadow: "0 10px 28px rgba(0,76,42,.35)",
              }}
            >
              <MKTypography
                component="h1"
                variant="h3"
                fontWeight="bold"
                color="white"
                mb={0.5}
                sx={{ textShadow: "0 2px 6px rgba(0,76,42,.85)" }}
              >
                Institucional 
              </MKTypography>
              <MKTypography
                variant="body1"
                color="white"
                sx={{ opacity: 0.95, textShadow: "0 1px 3px rgba(0,76,42,.8)" }}
              >
                Usinagem de precisão, inovação e compromisso com resultados.
              </MKTypography>
            </MKBox>
          </SectionContainer>
        </FadeIn>
      </MKBox>

      <SectionContainer sx={{ mt: -3, mb: 4 }}>
        <Breadcrumbs
          routes={[{ label: "Home", route: "/" }, { label: "Institucional" }]}
          sx={{ "& .MuiBreadcrumbs-separator": { color: BRAND.main } }}
        />
      </SectionContainer>

      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <FadeIn show={loaded} timeout={700}>
          <NossaHistoria />
        </FadeIn>

        <NossaFilosofia backgroundImage={bgFilosofia} />
      </Card>

      <WhatsAppFloat />
    </>
  );
}
